
import React, { useState, useEffect, useCallback } from 'react';
import { motion, useAnimation } from 'motion/react';
import { RefreshCw } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, children }) => {
  const [startY, setStartY] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const controls = useAnimation();

  const PULL_THRESHOLD = 80;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY === 0) {
      setStartY(e.touches[0].pageY);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (window.scrollY === 0 && !isRefreshing) {
      const currentY = e.touches[0].pageY;
      const diff = Math.max(0, (currentY - startY) * 0.4);
      if (diff > 0) {
        setPullDistance(diff);
      }
    }
  };

  const handleTouchEnd = async () => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(PULL_THRESHOLD);
      
      if (window.navigator.vibrate) {
        window.navigator.vibrate(20);
      }

      await onRefresh();
      
      setIsRefreshing(false);
      setPullDistance(0);
    } else {
      setPullDistance(0);
    }
  };

  return (
    <div 
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative"
    >
      <div 
        className="absolute top-0 left-0 right-0 flex justify-center pointer-events-none z-[45]"
        style={{ height: pullDistance }}
      >
        <motion.div
          animate={{ 
            rotate: isRefreshing ? 360 : pullDistance * 2,
            scale: pullDistance > 20 ? 1 : 0,
            opacity: pullDistance > 20 ? 1 : 0
          }}
          transition={isRefreshing ? { repeat: Infinity, duration: 1, ease: "linear" } : { type: "spring", stiffness: 300, damping: 30 }}
          className="mt-4 bg-white shadow-xl rounded-full p-2 border border-slate-100"
        >
          <RefreshCw size={20} className="text-indigo-600" />
        </motion.div>
      </div>

      <motion.div
        animate={{ y: isRefreshing ? PULL_THRESHOLD : pullDistance }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {children}
      </motion.div>
    </div>
  );
};
